/* eslint-disable react/display-name */
import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react'
import {
    Alert,
    Chip,
    Grid,
    Stack,
    Box,
    IconButton,
    TextField,
    InputAdornment,
    Checkbox,
    Radio,
    FormControlLabel,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCircle,
    faTimes,
    faHammer,
    faChevronDown,
} from '@fortawesome/pro-duotone-svg-icons'
import LoadingDefault from '../Elements/Loading/LoadingDefault'
import SelectRC, { Option } from 'rc-select'
import { useListFieldData } from './hooks/useListFieldData'
import { BadgeTag } from '../Elements/StyledComponents/Tags'
import {
    selectionControlLabelProps,
    selectionControlSx,
} from '../Elements/StyledComponents/Input'

interface ListFieldProps {
    item: any
    value: any
    mode: string
    dossier?: any
    errors?: string
    changeValue?: (id: string, value: any) => void
    recheckConditions?: () => void
    shoudListenForConditions?: boolean
    kedo: any
}

interface ListFieldRef {
    doFocus: () => void
    getValue: () => number[]
}

const ListField = forwardRef<ListFieldRef, ListFieldProps>((props, ref) => {
    const innerRef = useRef<HTMLDivElement>(null)
    const [opened, setOpened] = useState<boolean>(false)

    const {
        options,
        selected,
        search,
        optionsLoading,
        fastSwitchLoading,
        isAdmin,
        setSearch,
        setSelected,
        addSelectedItem,
        removeSelectedItem,
        setSingleSelectByOptionId,
        getSelectableOptions,
        getCurrentTranslationItemText,
        isSingleSelect,
        listOptionsHasVisibilitySettings,
        onFastSwitchChange,
    } = useListFieldData({
        item: props.item,
        value: props.value,
        mode: props.mode,
        dossier: props.dossier,
        kedo: props.kedo,
        shoudListenForConditions: props.shoudListenForConditions,
        recheckConditions: props.recheckConditions,
        changeValue: props.changeValue,
    })

    const singleSelectSx = {
        '& .rc-select-single .rc-select-selector': {
            display: 'flex',
            alignItems: 'center',
        },
        '& .rc-select-single .rc-select-selector .rc-select-selection-item': {
            top: '50%',
            transform: 'translateY(-50%)',
        },
    } as const
    const checkboxViewLabelSx = {
        ...selectionControlLabelProps.sx,
        '& .MuiFormControlLabel-label': {
            fontWeight: 700,
        },
    }

    const doFocus = () => {
        if (innerRef.current) {
            setTimeout(() => innerRef.current?.focus(), 1)
            window.scrollTo(0, innerRef.current.offsetTop)
        }
    }

    const getValue = (): number[] => {
        return selected.map((item) => item.id)
    }

    const isFastSwitch = (): boolean => {
        if (!props.item || !props.item.settings) {
            return false
        }
        if (props.item.settings.fast_switch === true) {
            return true
        }
        return false
    }

    const renderShow = () => {
        const locale = props.kedo.env().getSelectedLocale()

        if (isSingleSelect()) {
            if (selected.length <= 0) {
                return ''
            }

            const trans = getCurrentTranslationItemText(selected[0], locale)

            return (
                <span>
                    {selected[0].color
                        ? getColor(selected[0].color, trans)
                        : trans}
                </span>
            )
        }

        return (
            <ul>
                {selected.map((item) => {
                    const trans = getCurrentTranslationItemText(item, locale)
                    return (
                        <li key={item.id}>
                            {item.color ? getColor(item.color, trans) : trans}
                        </li>
                    )
                })}
            </ul>
        )
    }

    const searchOption = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value)
    }

    const getColor = (backgroundColor: string, text: string) => {
        return (
            <BadgeTag
                color={backgroundColor}
                textColor="#fff"
                size="small"
                clickable
            >
                {text}
            </BadgeTag>
        )
    }

    const isInvalid = (): boolean => {
        if (props.errors && props.errors.length > 0) {
            return true
        }

        return false
    }

    const renderFastSwitch = () => {
        const locale = props.kedo.env().getSelectedLocale()
        const roles = props.kedo.env().getCurrentEnvironmentRoles()

        const selectOptions: any[] = []
        options.forEach((item) => {
            if (item?.settings?.roles.length > 0) {
                if (
                    roles?.filter((roleItem: any) =>
                        item?.settings?.roles.includes(roleItem.id)
                    ).length > 0 &&
                    isAdmin !== true
                ) {
                    return
                }
            }

            let iconContent = null
            if (
                isAdmin &&
                item.settings &&
                item.settings.roles &&
                item.settings.roles.length > 0
            ) {
                iconContent = <FontAwesomeIcon icon={faHammer} />
            }

            const trans = getCurrentTranslationItemText(item, locale)

            selectOptions.push({
                id: item.id,
                value: getCurrentTranslationItemText(item, locale),
                label: (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            '& svg': {
                                ml: 1,
                            },
                        }}
                    >
                        {item.color ? getColor(item.color, trans) : trans}
                        {iconContent}
                    </Box>
                ),
                color: item.color,
                translations: item.translations,
            })
        })

        const currentItem: any[] = []

        if (selected) {
            selected.forEach((item) => {
                let iconContent = null
                const isVisibilityValid = listOptionsHasVisibilitySettings(
                    item.id
                )

                if (isAdmin && isVisibilityValid == true) {
                    iconContent = <FontAwesomeIcon icon={faHammer} />
                }

                const trans = getCurrentTranslationItemText(item, locale)

                currentItem.push({
                    id: item.id,
                    value: getCurrentTranslationItemText(item, locale),
                    color: item.color,
                    label: (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                '& svg': {
                                    ml: 1,
                                },
                            }}
                            className={'select-input'}
                        >
                            {item.color ? getColor(item.color, trans) : trans}
                            {iconContent}
                        </Box>
                    ),
                })
            })
        }

        const filteredOptions = selectOptions.filter(
            (option) =>
                !currentItem.some((selected) => selected.value === option.value)
        )

        return (
            <Box ref={innerRef} sx={singleSelectSx}>
                {fastSwitchLoading ? (
                    <LoadingDefault />
                ) : (
                    <>
                        {!isSingleSelect() ? (
                            <SelectRC
                                open={opened}
                                onDropdownVisibleChange={(visible) =>
                                    setOpened(visible)
                                }
                                mode={
                                    isSingleSelect() === false
                                        ? 'multiple'
                                        : undefined
                                }
                                allowClear={isSingleSelect() === true}
                                className="w-100 custom-select-react fast-switch"
                                value={currentItem.map((item) => ({
                                    value: item.value,
                                    label: item.label,
                                }))}
                                notFoundContent={props.kedo.t('No items found')}
                                onChange={(selectedItems: any) => {
                                    const selectedOptions = selectedItems.map(
                                        (selectedItem: any) =>
                                            selectOptions.find(
                                                (option) =>
                                                    option.value ===
                                                    selectedItem.value
                                            )
                                    )
                                    onFastSwitchChange(
                                        selectedOptions ? selectedOptions : []
                                    )
                                }}
                                suffixIcon={
                                    <FontAwesomeIcon icon={faChevronDown} />
                                }
                                placeholder={props.kedo.t('Search') + '...'}
                                labelInValue
                            >
                                {filteredOptions.map((option) => (
                                    <Option
                                        key={option.id}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </Option>
                                ))}
                            </SelectRC>
                        ) : (
                            <SelectRC
                                className="w-100 custom-select-react no-border fastList"
                                id={`${props.item.id}-search`}
                                allowClear
                                onChange={(selectedItem: any) => {
                                    if (!selectedItem) {
                                        onFastSwitchChange([])
                                        return
                                    }
                                    const selectedOption = selectOptions.find(
                                        (option) =>
                                            option.value === selectedItem.value
                                    )

                                    onFastSwitchChange(
                                        selectedOption ? selectedOption : []
                                    )
                                }}
                                suffixIcon={
                                    <FontAwesomeIcon icon={faChevronDown} />
                                }
                                placeholder={props.kedo.t('Search') + '...'}
                                value={
                                    currentItem.length > 0
                                        ? {
                                              value: currentItem[0].value,
                                              label: currentItem[0].label,
                                          }
                                        : undefined
                                }
                                labelInValue
                                notFoundContent={props.kedo.t('No items found')}
                            >
                                {filteredOptions.map((option) => (
                                    <Option
                                        key={option.id}
                                        value={option.value}
                                        disabled={option.disabled}
                                    >
                                        {option.label}
                                    </Option>
                                ))}
                            </SelectRC>
                        )}
                    </>
                )}
                {isInvalid() ? (
                    <span className={'fake-feedback'}>{props.errors}</span>
                ) : null}
            </Box>
        )
    }

    const renderList = () => {
        if (isFastSwitch()) {
            return renderFastSwitch()
        }

        const locale = props.kedo.env().getSelectedLocale()

        return (
            <ul className={'list-group list-group-flush'}>
                {selected.map((item) => {
                    const trans = getCurrentTranslationItemText(item, locale)
                    return (
                        <li
                            className={'list-group-item'}
                            style={{ backgroundColor: 'transparent' }}
                            key={item.id}
                        >
                            {item.color ? getColor(item.color, trans) : trans}
                        </li>
                    )
                })}
            </ul>
        )
    }

    const renderEdit = () => {
        if (optionsLoading) {
            return <LoadingDefault />
        }

        const roles = props.kedo.env().getCurrentEnvironmentRoles()
        const locale = props.kedo.env().getSelectedLocale()
        const selectionFull = isSingleSelect() === true && selected.length > 0

        if (isSingleSelect()) {
            const selectOptions: any[] = []
            options.forEach((item) => {
                if (item?.settings?.roles.length > 0) {
                    if (
                        roles?.filter((roleItem: any) =>
                            item?.settings?.roles.includes(roleItem.id)
                        ).length > 0 &&
                        isAdmin !== true
                    ) {
                        return
                    }
                }

                selectOptions.push({
                    id: item.id,
                    value: getCurrentTranslationItemText(item, locale),
                    label: (
                        <div>
                            {item.color
                                ? getColor(
                                      item.color,
                                      getCurrentTranslationItemText(
                                          item,
                                          locale
                                      )
                                  )
                                : getCurrentTranslationItemText(item, locale)}
                        </div>
                    ),
                    color: item.color,
                    translations: item.translations,
                })
            })

            const currentItem: any[] = []
            if (selected) {
                selected.forEach((item) => {
                    currentItem.push({
                        id: item.id,
                        value: getCurrentTranslationItemText(item, locale),
                        color: item.color,
                        label: (
                            <div className={'select-input'}>
                                {item.color
                                    ? getColor(
                                          item.color,
                                          getCurrentTranslationItemText(
                                              item,
                                              locale
                                          )
                                      )
                                    : getCurrentTranslationItemText(
                                          item,
                                          locale
                                      )}
                            </div>
                        ),
                    })
                })
            }

            const filteredOptions = selectOptions.filter(
                (option) =>
                    !currentItem.some(
                        (selected) => selected.value === option.value
                    )
            )

            return (
                <Box ref={innerRef} sx={singleSelectSx}>
                    {props.item.settings?.checkbox_view ? (
                        <Grid
                            container
                            direction={
                                props.item?.settings?.checkbox_view_horizontal
                                    ? 'row'
                                    : 'column'
                            }
                            wrap="wrap"
                            alignItems={
                                props.item?.settings?.checkbox_view_horizontal
                                    ? 'center'
                                    : 'flex-start'
                            }
                            spacing={1}
                        >
                            {optionsLoading ? (
                                <li className={'list-group-item'}>
                                    <LoadingDefault />
                                </li>
                            ) : null}
                            {selectOptions.map((item) => (
                                <Grid
                                    size={12}
                                    key={item.id}
                                >
                                    <FormControlLabel
                                        {...selectionControlLabelProps}
                                        sx={checkboxViewLabelSx}
                                        control={
                                            <Radio
                                                size="small"
                                                sx={selectionControlSx}
                                                checked={
                                                    selected?.[0]?.id == item.id
                                                }
                                                id={item.id.toString()}
                                                name={String(
                                                    props.item
                                                        .def_dossier_def_field.id
                                                )}
                                                onChange={() =>
                                                    setSingleSelectByOptionId(
                                                        item
                                                    )
                                                }
                                            />
                                        }
                                        label={
                                            item.color
                                            ? getColor(item.color, item.value)
                                            : item.value
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <SelectRC
                            open={opened}
                            onDropdownVisibleChange={(visible) =>
                                setOpened(visible)
                            }
                            className={`w-100 custom-select-react no-border ${
                                isInvalid() ? 'border-red' : ''
                            }`}
                            id={`${props.item.id}-search`}
                            allowClear
                            showSearch
                            onChange={(selectedItem: any) => {
                                if (selectedItem) {
                                    const selectedOption = selectOptions.find(
                                        (option) =>
                                            option.value === selectedItem.value
                                    )
                                    setSingleSelectByOptionId(selectedOption)
                                } else {
                                    setSingleSelectByOptionId(null)
                                }
                            }}
                            suffixIcon={
                                <FontAwesomeIcon icon={faChevronDown} />
                            }
                            placeholder={props.kedo.t('Search') + '...'}
                            value={
                                currentItem.length > 0
                                    ? {
                                          value: currentItem[0].value,
                                          label: currentItem[0].label,
                                      }
                                    : undefined
                            }
                            labelInValue
                            notFoundContent={props.kedo.t('No items found')}
                        >
                            {filteredOptions.map((option) => (
                                <Option
                                    key={option.id}
                                    value={option.value}
                                    disabled={option.disabled}
                                >
                                    {option.label}
                                </Option>
                            ))}
                        </SelectRC>
                    )}
                    {isInvalid() ? (
                        <span className={'fake-feedback'}>{props.errors}</span>
                    ) : null}
                </Box>
            )
        }

        return (
            <>
                {props.item.settings && props.item.settings.checkbox_view ? (
                    <Grid
                        container
                        direction={
                            props.item?.settings?.checkbox_view_horizontal
                                ? 'row'
                                : 'column'
                        }
                        wrap="wrap"
                        alignItems={
                            props.item?.settings?.checkbox_view_horizontal
                                ? 'center'
                                : 'flex-start'
                        }
                        spacing={1}
                    >
                        {optionsLoading ? (
                            <li className={'list-group-item'}>
                                <LoadingDefault />
                            </li>
                        ) : null}
                        {options.map((item) => {
                            if (item?.settings?.roles.length > 0) {
                                if (
                                    roles?.filter((roleItem: any) =>
                                        item?.settings?.roles.includes(
                                            roleItem.id
                                        )
                                    ).length > 0 &&
                                    isAdmin !== true &&
                                    !selected.find(
                                        (select) => select.id == item.id
                                    )
                                ) {
                                    return null
                                }
                            }

                            const trans = getCurrentTranslationItemText(
                                item,
                                locale
                            )

                            return (
                                <Grid
                                    size={12}
                                    key={item.id}
                                >
                                    <FormControlLabel
                                        {...selectionControlLabelProps}
                                        sx={checkboxViewLabelSx}
                                        control={
                                            <Checkbox
                                                size="small"
                                                sx={selectionControlSx}
                                                checked={
                                                    !!selected.find(
                                                        (select) =>
                                                            select.id == item.id
                                                    )
                                                }
                                                id={item.id.toString()}
                                                onChange={(event) => {
                                                    if (event.target.checked) {
                                                        addSelectedItem(item)
                                                    } else {
                                                        removeSelectedItem(item)
                                                    }
                                                }}
                                            />
                                        }
                                        label={
                                            item.color
                                            ? getColor(item.color, trans)
                                            : trans
                                        }
                                    />
                                </Grid>
                            )
                        })}
                    </Grid>
                ) : (
                    <>
                        {isInvalid() ? (
                            <Alert variant={'standard'}>{props.errors}</Alert>
                        ) : null}
                        <div className="input-group">
                            <TextField
                                fullWidth
                                slotProps={{
                                    input: {
                                        readOnly: optionsLoading || selectionFull,
                                        endAdornment: search ? (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setSearch(null)}
                                                    sx={{
                                                        color: 'grey.700',
                                                        p: 0.5,
                                                    }}
                                                    aria-label="clear"
                                                    edge="end"
                                                    size="small"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faTimes}
                                                    />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                    htmlInput: {
                                        'aria-describedby': 'button-addon4',
                                    },
                                }}
                                value={search ? search : ''}
                                onChange={(event) =>
                                    searchOption(
                                        event as React.ChangeEvent<HTMLInputElement>
                                    )
                                }
                                placeholder={
                                    selectionFull
                                        ? props.kedo.t(
                                              'Maximum number of items selected'
                                          )
                                        : props.kedo.t('Search an option')
                                }
                                size="small"
                                sx={{ flex: 1 }}
                            />
                        </div>
                        <div className={'list-group list-group-flush'}>
                            {optionsLoading ? (
                                <li className={'list-group-item'}>
                                    <LoadingDefault />
                                </li>
                            ) : null}

                            {getSelectableOptions(
                                options,
                                selected,
                                search
                            ).map((item) => {
                                if (item?.settings?.roles.length > 0) {
                                    if (
                                        roles?.filter((roleItem: any) =>
                                            item?.settings?.roles.includes(
                                                roleItem.id
                                            )
                                        ).length > 0 &&
                                        isAdmin !== true
                                    ) {
                                        return null
                                    }
                                }

                                const trans = getCurrentTranslationItemText(
                                    item,
                                    locale
                                )

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => addSelectedItem(item)}
                                        className={
                                            'list-group-item list-group-item-action'
                                        }
                                    >
                                        {item.color
                                            ? getColor(item.color, trans)
                                            : trans}
                                    </button>
                                )
                            })}
                        </div>
                        <hr />
                        <strong>{props.kedo.t('Selected')}</strong>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ flexWrap: 'wrap', gap: '8px', mt: 1 }}
                        >
                            {selected.map((item) => {
                                const trans = getCurrentTranslationItemText(
                                    item,
                                    locale
                                )
                                return (
                                    <Chip
                                        key={`selected-chip-${item.id}`}
                                        label={
                                            item.color
                                                ? getColor(item.color, trans)
                                                : trans
                                        }
                                        onDelete={() =>
                                            removeSelectedItem(item)
                                        }
                                        color="primary"
                                        variant="filled"
                                        sx={{
                                            backgroundColor:
                                                item.color || 'primary.main',
                                            color: item.color
                                                ? '#fff'
                                                : 'primary.contrastText',
                                            '& .MuiChip-deleteIcon': {
                                                color: item.color
                                                    ? 'rgba(255,255,255,0.7)'
                                                    : 'primary.contrastText',
                                                '&:hover': {
                                                    color: '#fff',
                                                },
                                            },
                                        }}
                                        deleteIcon={
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                style={{ fontSize: '0.75rem' }}
                                            />
                                        }
                                    />
                                )
                            })}
                        </Stack>
                    </>
                )}
            </>
        )
    }

    const changeSearchOptions = (event: any) => {
        const options: any[] = []

        if (!event || !event.map) {
            if (props.changeValue) {
                props.changeValue(props.item.id, [])
            }

            setSelected([])
            return
        }

        event.map((item: any) => options.push({ id: item.id }))

        if (props.changeValue) {
            props.changeValue(props.item.id, options)
        }

        setSelected(event)
        setOpened(false)
    }

    const renderSearch = () => {
        const locale = props.kedo.env().getSelectedLocale()
        const newSelected: any[] = []
        const searchoptions: any[] = []

        options.map((item) =>
            searchoptions.push({
                id: item.id,
                value: getCurrentTranslationItemText(item, locale),
                label: (
                    <div>
                        {item.color ? (
                            <span>
                                <FontAwesomeIcon
                                    style={{ color: item.color }}
                                    icon={faCircle}
                                />
                                &nbsp;{' '}
                            </span>
                        ) : null}
                        {getCurrentTranslationItemText(item, locale)}
                    </div>
                ),
                color: item.color,
                translations: item.translations,
            })
        )

        selected.forEach((selectedItem) => {
            const found = searchoptions.find(
                (item) => item.id === selectedItem.id
            )

            if (found) {
                newSelected.push(found)
            }
        })

        const filteredOptions = searchoptions.filter(
            (option) =>
                !newSelected.some((selected) => selected.value === option.value)
        )

        return (
            <SelectRC
                open={opened}
                className="w-100 custom-select-react"
                id={`${props.item.id}-search`}
                mode="multiple"
                allowClear
                onDropdownVisibleChange={(visible) => setOpened(visible)}
                onChange={(selectedItems: any) => {
                    const selectedOptions = selectedItems.map(
                        (selectedItem: any) =>
                            searchoptions.find(
                                (option) => option.value === selectedItem.value
                            )
                    )
                    changeSearchOptions(selectedOptions)
                }}
                suffixIcon={<FontAwesomeIcon icon={faChevronDown} />}
                placeholder={props.kedo.t('Search') + '...'}
                value={newSelected.map((item) => ({
                    value: item.value,
                    label: item.label,
                }))}
                labelInValue
                notFoundContent={props.kedo.t('No items found')}
            >
                {filteredOptions.map((option) => (
                    <Option key={option.id} value={option.value}>
                        {option.label}
                    </Option>
                ))}
            </SelectRC>
        )
    }

    useImperativeHandle(ref, () => ({
        doFocus,
        getValue,
    }))

    if (props.mode === 'edit' || props.mode === 'show/edit') {
        return renderEdit()
    } else if (props.mode === 'list') {
        return renderList()
    } else if (props.mode === 'search' || props.mode === 'advanced-search') {
        return renderSearch()
    }

    return renderShow()
})

export default ListField
